// pelanggan.view — daftar + form (anti-double WA) + detail (riwayat) + hapus. Port renderPelanggan v0.18.
import { esc, wa62, rp, fillTpl, maskPhone } from '@domain/format';
import { waTpl } from '@core/settings';
import { currentUser } from '@core/session';
import { bisaBuka } from '@core/perms';
import { waUrl, openWA } from '@core/wa';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { downloadFile, csvCell } from '@ui/download';
import type { Customer } from '@core/types';
import * as repo from './pelanggan.repo';
import * as svc from './pelanggan.service';
import { setPerekomendasi } from '@modules/crm/afiliasi.repo';
import * as crmRepo from '@modules/crm/crm.repo';
import { pointsEarned, poinRate } from '@modules/crm/crm.service';

const SUMBER = ['Walk-in', 'Repeat', 'Referral', 'Marketplace', 'WhatsApp', 'Instagram', 'TikTok', 'YouTube', 'Lainnya'];
let q = '';

export async function renderPelanggan(root: HTMLElement): Promise<void> {
  const u = currentUser()!;
  root.innerHTML = `<div class="ttl">👥 Pelanggan</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let list: Customer[]; let counts = new Map<number, number>();
  try { [list, counts] = await Promise.all([repo.listCustomers(), repo.ticketCounts()]); }
  catch (e) { root.innerHTML = `<div class="ttl">👥 Pelanggan</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const filtered = q
    ? list.filter((c) => `${c.nama ?? ''} ${c.telp ?? ''} ${c.wa ?? ''} ${c.kota ?? ''}`.toLowerCase().includes(q.toLowerCase()))
    : list;
  const canDel = ['Owner', 'Manager'].includes(u.role);
  // Baca pelanggan kini bisa lintas cabang (pelanggan cabang lain yang unitnya diservis di sini),
  // TAPI hak ubah tetap milik cabang pendaftar. Tanpa penjaga ini tombol ✏️ tampil lalu gagal
  // DIAM-DIAM: PostgREST yang ditolak RLS mengubah 0 baris tanpa error → petugas mengira tersimpan.
  const canEdit = (c: Customer): boolean =>
    ['Owner', 'Manager'].includes(u.role) || !c.outlet || c.outlet === u.outlet;
  const btn = 'class="ico-btn" style="font-size:15px"';
  const katPill = (k: string): string => `<span class="pill" style="${k === 'Toko' ? 'background:var(--ice);color:var(--navy)' : 'background:#eef2f6;color:var(--muted)'}">${esc(k)}</span>`;
  // Izin '📞 Lihat No. HP pelanggan' (Hak Akses): dicabut → nomor disensor & tak bisa diklik ke WA,
  // supaya kontak pelanggan tak bisa dipanen dari layar oleh peran yang tak berkepentingan.
  const bolehTelepon = bisaBuka('pelanggan.telepon');
  const waLink = (c: Customer): string => { const w = (c.wa ?? c.telp ?? '').replace(/\D/g, ''); if (!w) return '<span style="color:var(--muted)">—</span>'; return bolehTelepon ? `<a href="${waUrl(w)}" target="_blank">${esc(c.wa ?? c.telp ?? '')}</a>` : `<span style="color:var(--muted)" title="Nomor disembunyikan">${esc(maskPhone(w))}</span>`; };
  const rowsHtml = filtered.map((c) => `<tr><td><b>${esc(c.nama)}</b></td><td>${katPill(c.kat)}</td><td>${waLink(c)}</td><td>${esc(c.kota ?? '—')}</td><td style="text-align:center">${counts.get(c.id) ?? 0}</td><td style="white-space:nowrap"><button ${btn} title="Detail" data-detail="${c.id}">🔍</button> ${canEdit(c) ? `<button ${btn} title="Ubah" data-edit="${c.id}">✏️</button>` : `<span title="Pelanggan terdaftar di cabang ${esc(c.outlet ?? '—')} — ubah data lewat cabang tersebut" style="color:var(--muted);font-size:13px">🔒</span>`}${canDel ? ` <button ${btn} title="Hapus" data-del="${c.id}">🗑️</button>` : ''}</td></tr>`).join('')
    || `<tr><td colspan="6" class="empty">${q ? 'Tidak ada hasil.' : 'Belum ada pelanggan.'}</td></tr>`;

  root.innerHTML = `<div class="ttl">👥 Pelanggan <span class="pill" style="background:var(--ice);color:var(--navy)">${list.length}</span></div>
    <div class="scope-note">Kelola data pelanggan · anti-double via nomor WA · Total Servis per pelanggan.</div>
    <div class="toolbar">
      <input id="c_search" placeholder="Cari nama / no / kota…" value="${esc(q)}" style="flex:1;min-width:200px">
      <button class="btn" id="c_export">⬇️ Export CSV</button>
      <button class="btn primary" id="c_add">➕ Pelanggan Baru</button></div>
    <table class="data"><thead><tr><th>Nama</th><th>Kategori</th><th>WhatsApp</th><th>Kota</th><th>Servis</th><th>Aksi</th></tr></thead><tbody>${rowsHtml}</tbody></table>`;

  const refresh = (): void => { void renderPelanggan(root); };
  const s = root.querySelector('#c_search') as HTMLInputElement;
  s.addEventListener('input', () => {
    q = s.value; const p = s.selectionStart;
    void renderPelanggan(root).then(() => { const n = root.querySelector('#c_search') as HTMLInputElement | null; if (n) { n.focus(); n.setSelectionRange(p, p); } });
  });
  (root.querySelector('#c_add') as HTMLElement).addEventListener('click', () => custForm(null, refresh));
  (root.querySelector('#c_export') as HTMLElement).addEventListener('click', () => exportCSV(filtered));
  root.querySelectorAll<HTMLElement>('button[data-edit]').forEach((b) =>
    b.addEventListener('click', () => { const c = list.find((x) => x.id === +(b.dataset.edit ?? 0)); if (c) custForm(c, refresh); }));
  root.querySelectorAll<HTMLElement>('button[data-detail]').forEach((b) =>
    b.addEventListener('click', () => void custDetail(+(b.dataset.detail ?? 0))));
  root.querySelectorAll<HTMLElement>('button[data-del]').forEach((b) =>
    b.addEventListener('click', async () => {
      const c = list.find((x) => x.id === +(b.dataset.del ?? 0)); if (!c) return;
      if (!confirm(`Hapus pelanggan "${c.nama}"?`)) return;
      try { await svc.deleteCust(c.id); toast('🗑️ Dihapus'); refresh(); } catch (e) { toast((e as Error).message); }
    }));
}

/** Export daftar pelanggan ke CSV (Excel-friendly, BOM UTF-8). Port exportCust v0.18.
 *  Lewat downloadFile → tunduk guard export (database pelanggan = target pencurian utama). */
function exportCSV(list: Customer[]): void {
  const head = ['Nama', 'Kategori', 'Telepon', 'WhatsApp', 'Kota', 'Email', 'Lahir', 'Sumber', 'Alamat'];
  // Kontak hanya ikut terunduh bila izin '📞 Lihat No. HP pelanggan' dipegang — kalau tidak, file
  // keluar tanpa telp/WA/email/alamat (mencegah daftar kontak dibawa keluar lewat export).
  const bolehKontak = bisaBuka('pelanggan.telepon');
  const rows = list.map((c) => (bolehKontak
    ? [c.nama, c.kat, c.telp ?? '', c.wa ?? '', c.kota ?? '', c.email ?? '', c.lahir ?? '', c.sumber ?? '', c.alamat ?? '']
    : [c.nama, c.kat, '', '', c.kota ?? '', '', c.lahir ?? '', c.sumber ?? '', '']).map(csvCell).join(','));
  const csv = [head.map(csvCell).join(','), ...rows].join('\r\n');
  void downloadFile('﻿' + csv, 'pelanggan.csv'); // downloadFile → guard export; csvCell → anti formula-injection
}

function custForm(c: Customer | null, done: () => void): void {
  const isNew = !c;
  const bg = openModal(`<div class="modal-head"><h3>${isNew ? 'Buat' : 'Ubah'} Pelanggan</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="field"><label>Nama <span class="req">*</span></label><input id="cf_nama" value="${esc(c?.nama ?? '')}"></div>
      <div class="row2">
        <div class="field"><label>Kategori <span class="req">*</span></label><select id="cf_kat"><option value="">— pilih —</option><option ${c?.kat === 'User' ? 'selected' : ''}>User</option><option ${c?.kat === 'Toko' ? 'selected' : ''}>Toko</option></select></div>
        <div class="field"><label>Sumber</label><select id="cf_sumber"><option value="">—</option>${SUMBER.map((x) => `<option ${c?.sumber === x ? 'selected' : ''}>${x}</option>`).join('')}</select></div>
      </div>
      <div class="field" id="cf_refbox" style="display:none"><label>Direkomendasikan oleh</label>
        <input id="cf_refcari" placeholder="ketik nama / No. HP pelanggan yang mengajak" autocomplete="off">
        <div id="cf_refhasil" style="max-height:150px;overflow:auto"></div>
        <div id="cf_refpilih" class="hint"></div></div>
      <div class="field"><label>No. HP / WhatsApp <span class="req">*</span> <span style="color:var(--muted);font-weight:400">(auto +62 · anti-double)</span></label><input id="cf_wa" value="${esc(c?.wa ?? c?.telp ?? '')}" inputmode="numeric" placeholder="0812…"><div id="cf_dup" style="margin-top:4px"></div></div>
      <div class="row2"><div class="field"><label>Tanggal Lahir</label><input id="cf_lahir" type="date" value="${esc(c?.lahir ?? '')}"></div><div class="field"><label>Email</label><input id="cf_email" value="${esc(c?.email ?? '')}"></div></div>
      <div class="field"><label>Kota</label><input id="cf_kota" value="${esc(c?.kota ?? '')}"></div>
      <div class="field"><label>Alamat</label><textarea id="cf_alamat">${esc(c?.alamat ?? '')}</textarea></div>
      <div class="field"><label>Catatan</label><textarea id="cf_catatan">${esc(c?.catatan ?? '')}</textarea></div>
      <div class="err"></div></div>
    <div class="modal-foot"><button class="btn" id="c">Batal</button><button class="btn primary" id="s">Simpan</button></div>`);

  // Perekomendasi hanya ditanyakan bila sumbernya Referral — supaya form tak ramai untuk
  // pelanggan biasa. Bonusnya sendiri baru cair setelah servis pertamanya lunas & diverifikasi.
  let refId: number | null = null;
  const refBox = bg.querySelector('#cf_refbox') as HTMLElement;
  const refCari = bg.querySelector('#cf_refcari') as HTMLInputElement;
  const refHasil = bg.querySelector('#cf_refhasil') as HTMLElement;
  const refPilih = bg.querySelector('#cf_refpilih') as HTMLElement;
  const sumberEl = bg.querySelector('#cf_sumber') as HTMLSelectElement;
  const tampilRef = (): void => { refBox.style.display = sumberEl.value === 'Referral' ? '' : 'none'; };
  sumberEl.addEventListener('change', tampilRef); tampilRef();
  let refTimer: number | undefined;
  refCari.addEventListener('input', () => {
    window.clearTimeout(refTimer);
    const q = refCari.value.trim();
    refId = null; refPilih.textContent = '';
    if (q.length < 2) { refHasil.innerHTML = ''; return; }
    refTimer = window.setTimeout(async () => {
      try {
        const hasil = (await repo.listCustomers()).filter((x) => x.id !== c?.id &&
          `${x.nama} ${x.wa ?? ''} ${x.telp ?? ''}`.toLowerCase().includes(q.toLowerCase())).slice(0, 6);
        refHasil.innerHTML = hasil.map((x) => `<button type="button" class="btn cf-refpick" data-id="${x.id}" data-n="${esc(x.nama)}" style="display:block;width:100%;text-align:left;padding:5px 8px;margin:2px 0;font-size:12.5px">${esc(x.nama)} · ${esc(x.wa ?? x.telp ?? '—')}</button>`).join('')
          || '<div class="hint">Tidak ada yang cocok.</div>';
        refHasil.querySelectorAll<HTMLElement>('.cf-refpick').forEach((b) => b.addEventListener('click', () => {
          refId = +(b.dataset['id'] ?? 0);
          refPilih.innerHTML = `✔ Perekomendasi: <b>${esc(b.dataset['n'] ?? '')}</b>`;
          refHasil.innerHTML = ''; refCari.value = b.dataset['n'] ?? '';
        }));
      } catch { refHasil.innerHTML = ''; }
    }, 300);
  });

  const waEl = bg.querySelector('#cf_wa') as HTMLInputElement;
  const dupBox = bg.querySelector('#cf_dup') as HTMLElement;
  let dupTimer: number | undefined;
  const okConv = (w: string): string => `<span style="color:var(--ok);font-size:12px">✓ Convert WhatsApp: <b>+${esc(w)}</b></span>`;
  waEl.addEventListener('input', () => {
    const wa = wa62(waEl.value);
    window.clearTimeout(dupTimer);
    if (!wa) { dupBox.innerHTML = ''; return; }
    dupBox.innerHTML = okConv(wa); // convert ke WA (+62) langsung tampil — permintaan Apo
    dupTimer = window.setTimeout(() => {
      void repo.findByWA(wa).then((f) => {
        dupBox.innerHTML = f && f.id !== (c?.id ?? null) ? `<span style="color:var(--warn);font-size:12px">⚠️ Nomor sudah dipakai: <b>${esc(f.nama)}</b> — jangan buat double</span>` : okConv(wa);
      }).catch(() => { /* abaikan */ });
    }, 400);
  });
  (bg.querySelector('#c') as HTMLElement).addEventListener('click', () => bg.remove());
  const save = bg.querySelector('#s') as HTMLButtonElement;
  save.addEventListener('click', async () => {
    const g = (id: string): string => (bg.querySelector('#cf_' + id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
    const err = bg.querySelector('.err') as HTMLElement; err.textContent = ''; save.disabled = true;
    try {
      const savedId = await svc.saveCust({ id: c?.id ?? null, nama: g('nama'), kat: g('kat'), telpwa: g('wa'), lahir: g('lahir'), email: g('email'), kota: g('kota'), alamat: g('alamat'), sumber: g('sumber'), catatan: g('catatan') });
      // Perekomendasi disimpan terpisah (kolom baru) — best-effort supaya pelanggan tetap
      // tersimpan walau migrasi afiliasi belum di-push.
      if (g('sumber') === 'Referral' && refId) {
        try { await setPerekomendasi(savedId ?? c?.id ?? 0, refId); } catch { toast('Pelanggan tersimpan, tapi perekomendasi gagal dicatat.'); }
      }
      toast('✅ Tersimpan'); bg.remove(); done();
    } catch (e) { err.textContent = (e as Error).message; save.disabled = false; }
  });
}

async function custDetail(id: number): Promise<void> {
  let d: Awaited<ReturnType<typeof repo.detail>>;
  try { d = await repo.detail(id); } catch (e) { toast((e as Error).message); return; }
  const c = d.customer;
  // Koneksi CRM: LTV + poin loyalti pelanggan (basis sama dgn menu CRM).
  let spend: crmRepo.Spend = { ltv: 0, trx: 0, last: null, per: [] }; let redeemed = 0;
  try { const [s, reds] = await Promise.all([crmRepo.spendForCustomer(id), crmRepo.redemptionsForCustomer(id)]); spend = s; redeemed = reds.reduce((a, r) => a + r.points, 0); } catch { /* loyalitas opsional */ }
  const earned = pointsEarned(spend.ltv), avail = Math.max(0, earned - redeemed);
  const wa = wa62(c.wa ?? c.telp ?? '');
  const info: [string, string][] = [
    ['Kategori', c.kat], ['WhatsApp', bisaBuka('pelanggan.telepon') ? (c.wa ?? c.telp ?? '—') : maskPhone(c.wa ?? c.telp)], ['Kota', c.kota ?? '—'],
    ['Alamat', c.alamat ?? '—'], ['Lahir', c.lahir ?? '—'], ['Email', c.email ?? '—'], ['Sumber', c.sumber ?? '—'],
  ];
  const infoHtml = info.map(([k, v]) => `<div class="info-row"><span class="k">${esc(k)}</span><span>${esc(v)}</span></div>`).join('');
  const loyal = `<div class="ttl" style="font-size:14px;margin:12px 0 4px">💎 Loyalitas (CRM)</div>
    <div class="stat-grid" style="margin-bottom:6px">
      <div class="stat"><div class="lbl">LTV (belanja)</div><div class="num" style="color:var(--ok)">${rp(spend.ltv)}</div></div>
      <div class="stat"><div class="lbl">Transaksi (bayar diakui)</div><div class="num">${spend.trx}</div></div>
      <div class="stat"><div class="lbl">Poin tersedia</div><div class="num" style="color:var(--navy)">${avail} <span style="font-size:11px;color:var(--muted)">(${earned}−${redeemed})</span></div></div>
    </div>
    <div class="info-row"><span class="k">1 poin / belanja</span><span>${rp(poinRate())}</span></div>`;
  const hist = d.tickets.length
    ? d.tickets.map((t) => `<div class="info-row"><span class="k">${esc(t.no)}</span><span>${esc(t.unit)} · ${esc(t.status)} · ${esc(t.tgl)}</span></div>`).join('')
    : '<div class="empty">Belum ada servis.</div>';
  const waHist = d.waHistory.length
    ? `<div class="ttl" style="font-size:14px;margin:12px 0 4px">📱 Riwayat Ganti Nomor (${d.waHistory.length})</div>${[...d.waHistory].reverse().map((h) => `<div class="info-row"><span class="k">${esc((h.at || '').slice(0, 10))}</span><span>${esc(h.old)} → <b>${esc(h.new)}</b> · oleh ${esc(h.by)}</span></div>`).join('')}`
    : '';
  const bg = openModal(`<div class="modal-head"><h3>${esc(c.nama)}</h3><button class="x">×</button></div>
    <div class="modal-body">${infoHtml}${loyal}<div class="ttl" style="font-size:14px;margin:12px 0 4px">Riwayat Servis (${d.tickets.length})</div>${hist}${waHist}</div>
    <div class="modal-foot"><button class="btn primary" id="x">Tutup</button>${wa && currentUser()?.role !== 'Teknisi' ? `<button class="btn" id="cd_wa">💬 WA</button>` : ''}</div>`);
  (bg.querySelector('#x') as HTMLElement).addEventListener('click', () => bg.remove());
  bg.querySelector('#cd_wa')?.addEventListener('click', () => openWA(wa, fillTpl(waTpl().pelangganKosong, { nama: c.nama })));
}
